import React, { useCallback, useEffect, useState } from 'react';

import {
  Box,
  Button,
  Flex,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  Tooltip,
  useMediaQuery,
} from '@chakra-ui/react';
import { usePathname } from 'next/navigation';
import { FiChevronDown } from 'react-icons/fi';
import { IoCloseOutline } from 'react-icons/io5';
import { Chain } from 'viem';
import { useAccount, useChains } from 'wagmi';

import { ChainWithCustomData, WagmiConfig } from '@/config/wagmi.config';
import { ETH } from '@/constants/constants';
import { emitEvent, useDappSDKProvider } from '@/dapp-sdk-v2';
import BaseImage from '@/dapp-sdk-v2/components/Image';
import { EVENTS } from '@/dapp-sdk-v2/constants/events';
import useIsMobile from '@/hooks/useMobile';
import { useChainData } from '@/providers/ChainDataProvider';
import { getCurrentChain } from '@/utils/common';

interface NetworkProps {
  chain_name: string;
  is_native: boolean;
  chain_id: string;
  icon_key: string;
}

interface NetworkSwitchProps {
  onSwitch?: (data: NetworkProps) => void;
}

const NetworkSwitch = (_props: NetworkSwitchProps) => {
  const pathname = usePathname();
  const token = getCurrentChain(pathname);
  const [isLargeDevice] = useMediaQuery('(min-width: 992px)');

  const { isMobile } = useIsMobile();

  const { isEmbed, isSafeApp, setUserData } = useDappSDKProvider();
  const { chainId } = useAccount();
  const chains = useChains<WagmiConfig>() as readonly ChainWithCustomData[];
  const activeChain = chains.find((chain) => chain.id === chainId);
  const { switchChain } = useChainData();
  const [showTooltip, setShowTooltip] = useState(token === ETH);

  useEffect(() => {
    let isNative: any = window.localStorage.getItem(`${token}_is_native`);
    isNative = isNative !== 'false';

    setTimeout(() => {
      setUserData({
        isNativeChain: isNative,
      });
    }, 1000);

    // if (staking_network.chains) {
    //   staking_network.chains.forEach((item: NetworkProps) => {
    //     if (item.is_native === isNative) {
    //       setActiveChain(item);
    //     }
    //   });
    // }
  }, []);

  // useEffect(() => {
  //   if ((isEmbed || isSafeApp) && config) {
  //     // const network = staking_network.chains.find(
  //     //   (item: NetworkProps) => +item.chain_id === +config.chainId,
  //     // );
  //     //
  //     // if (!network) {
  //     //   return;
  //     // }
  //
  //     // setActiveChain(network);
  //     // setUserData({
  //     //   isNativeChain: network.is_native,
  //     // });
  //
  //     // window.localStorage.setItem(
  //     //   `${token}_is_native`,
  //     //   network.is_native ? 'true' : 'false',
  //     // );
  //   }
  // }, [isEmbed, isSafeApp, config]);

  // const isActiveChain = (data: NetworkProps) => {
  //   return data.chain_name === activeChain.chain_name;
  // };

  const handleSwitch = useCallback(
    (chain: Chain) => {
      switchChain(chain.id);
      emitEvent(EVENTS.HANDLE_SWITCH_NETWORK, chain);

      // setUserData({
      //   isNativeChain: data.is_native,
      // });
      // window.localStorage.setItem(
      //   `${token}_is_native`,
      //   data.is_native ? 'true' : 'false',
      // );
      // onSwitch && onSwitch(data);
    },
    [switchChain],
  );

  const closeTooltip = () => {
    setShowTooltip(false);
  };

  if (!isEmbed && !isSafeApp && chains.length > 1) {
    return (
      <>
        <Menu>
          <Tooltip
            isOpen={showTooltip}
            pointerEvents="all"
            label={
              <Flex
                alignItems="center"
                padding={{ base: '4px', lg: '12px' }}
                gap="8px"
              >
                <Text>Access L2 chains here</Text>
                <IconButton
                  size="18px"
                  bg="none"
                  icon={<IoCloseOutline size={18} />}
                  aria-label="Close tooltip"
                  cursor="pointer"
                  _hover={{ bg: 'none' }}
                  onClick={closeTooltip}
                />
              </Flex>
            }
            placement={isLargeDevice ? 'left' : 'bottom-start'}
            borderRadius={8}
            hasArrow
          >
            <MenuButton
              position="relative"
              as={Button}
              bg="backgroundSecondary"
              onClick={closeTooltip}
              color="textPrimary"
              h={{ base: '2rem', lg: '56px' }}
              w={{ base: '4rem', lg: 'fit-content' }}
              px={{ base: '0.375rem', lg: '1.5rem' }}
              _focus={{
                bg: 'backgroundSecondary',
              }}
              _hover={{
                bg: 'backgroundSecondary',
              }}
              rightIcon={
                <Box color="primary">
                  <FiChevronDown size={isMobile ? 16 : 24} />
                </Box>
              }
            >
              {activeChain ? (
                <>
                  <Flex gap="0.625rem" alignItems="center" width="auto">
                    <BaseImage
                      height={24}
                      width={24}
                      src={activeChain.custom?.iconUrl ?? ''}
                      alt={activeChain.name}
                    />
                    {!isMobile && <Text>{activeChain.name}</Text>}
                  </Flex>
                  {showTooltip && (
                    <Flex
                      position="absolute"
                      left="50%"
                      top={'-25%'}
                      onClick={closeTooltip}
                      transform="translate(-50%)"
                      bg="#2196F3"
                      color="white"
                      padding={{ base: '2px 4px', lg: '6px 8px' }}
                      fontSize={{ base: '8px', lg: '12px' }}
                      fontWeight={{ base: 600, lg: 700 }}
                      borderRadius={{ base: '2px', lg: '4px' }}
                    >
                      +{chains.length - 1} New
                    </Flex>
                  )}
                </>
              ) : (
                <Text>Select Chain</Text>
              )}
            </MenuButton>
          </Tooltip>

          <MenuList
            bg="backgroundSecondary"
            borderColor="seperator"
            borderRadius="0.75rem"
            p={2}
            gap={2}
          >
            {chains.map((item) => {
              return (
                <MenuItem
                  as="button"
                  key={item.id}
                  display="flex"
                  borderRadius="0.375rem"
                  p={2}
                  my={1}
                  alignItems="center"
                  gap="0.625rem"
                  onClick={() => handleSwitch(item)}
                  _focus={{
                    bg: 'backgroundPrimary',
                  }}
                  _hover={{
                    bg: 'backgroundPrimary',
                  }}
                >
                  <BaseImage
                    height={24}
                    width={24}
                    src={item.custom?.iconUrl ?? ''}
                    alt={item.name}
                  />
                  <Text>{item.name}</Text>
                </MenuItem>
              );
            })}
          </MenuList>
        </Menu>
      </>
    );
  }
  return null;
};

export default NetworkSwitch;
